import React from 'react';
import { StyleSheet, View } from 'react-native';
import { Text } from '../Texte';
import { LinearGradient } from 'expo-linear-gradient';
import { Apparition } from './Apparition';
import { t } from '../../i18n';
import { colors, gradients, radius, spacing, typography } from '../../theme/tokens';

export const ETIQUETTES_MAX = 8;

export interface EtiquettesMotsClesProps {
  /** Termes rendus par l'analyse — jamais une liste inventée. */
  termes: string[];
  /** Termes présents dans le CV adapté, une fois le CV revenu ; null avant. */
  couverts: string[] | null;
  /** Fin de préparation : moins d'étiquettes, les couvertes d'abord, la couverture en titre — le bouton
   *  « Vérifier ma candidature » prend la place, et l'écran doit tenir sans défiler. */
  synthese?: boolean;
}

// Deux lignes, « +N » compris : mesuré sur un iPhone SE (375 × 667), une troisième passait sous le bouton.
const ETIQUETTES_SYNTHESE = 3;

/**
 * Les mots-clés repérés par l'analyse, en étiquettes qui apparaissent l'une après l'autre (8 au
 * plus, puis « +N »). Quand le CV revient, ceux qu'il couvre passent en surbrillance.
 */
export function EtiquettesMotsCles({ termes, couverts, synthese = false }: EtiquettesMotsClesProps) {
  if (termes.length === 0) return null;
  const ordonnes = synthese && couverts ? [...termes.filter((x) => couverts.includes(x)), ...termes.filter((x) => !couverts.includes(x))] : termes;
  const visibles = ordonnes.slice(0, synthese ? ETIQUETTES_SYNTHESE : ETIQUETTES_MAX);
  const reste = termes.length - visibles.length;
  const couvert = (terme: string) => couverts?.includes(terme) ?? false;
  const libelle = couverts
    ? t('offres.preparation.motsClesCouvertsA11y', { couverts: couverts.length, total: termes.length, liste: termes.join(', ') })
    : t('offres.preparation.motsClesA11y', { count: termes.length, liste: termes.join(', ') });

  return (
    <View style={styles.bloc} accessible accessibilityLabel={libelle} testID="etiquettes-mots-cles">
      <Text style={styles.titre}>
        {synthese && couverts
          ? t('offres.preparation.couverture', { couverts: couverts.length, total: termes.length }).toLocaleUpperCase('fr-FR')
          : t('offres.preparation.motsClesTitre')}
      </Text>
      <View style={styles.liste}>
        {visibles.map((terme, i) => (
          <Apparition
            key={terme}
            effet="fondu"
            delai={i * 120}
            style={[styles.etiquette, couvert(terme) && styles.etiquetteCouverte]}
            testID={couvert(terme) ? `etiquette-couverte-${terme}` : `etiquette-${terme}`}
          >
            {couvert(terme) ? <LinearGradient colors={[...gradients.accent]} start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }} style={StyleSheet.absoluteFill} /> : null}
            <Text style={[styles.texte, couvert(terme) && styles.texteCouvert]} numberOfLines={1}>
              {terme}
            </Text>
          </Apparition>
        ))}
        {reste > 0 ? (
          <View style={styles.etiquette}>
            <Text style={styles.texte}>{t('offres.preparation.motsClesAutres', { count: reste })}</Text>
          </View>
        ) : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  bloc: { gap: spacing.xs },
  titre: { fontSize: typography.caption.size, lineHeight: typography.caption.lineHeight, letterSpacing: typography.caption.letterSpacing, fontWeight: '600', color: colors.grey400 },
  liste: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs },
  // Deux par ligne au plus : les vrais mots-clés sont longs (« Formation Bac+3 à Bac+5 … ») et,
  // entiers, ils prenaient chacun une ligne — l'écran ne tenait plus sur un petit iPhone.
  etiquette: {
    maxWidth: '48%',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: radius.pill,
    borderWidth: 1,
    borderColor: colors.grey400,
    overflow: 'hidden',
  },
  // Fond en dégradé d'accent (posé dans l'étiquette), sans contour.
  etiquetteCouverte: { borderColor: 'transparent' },
  texte: { fontSize: typography.small.size, lineHeight: typography.small.lineHeight, color: colors.grey200 },
  // Bleu nuit sur le dégradé cyan → électrique : 5,2:1 au plus faible (AA) ; le blanc n'y tient que 3,3:1.
  texteCouvert: { color: colors.night, fontWeight: '700' },
});
